import { graphql } from '@/gql';
import type {
  FetchPartnerNumbersAndMainPartnershipNumberQuery,
  FetchPartnerNumbersAndMainPartnershipNumberQueryVariables,
} from '@/gql/graphql';
import { queryExecute } from '@/Utils/graphqlAPI';
import type { PartnerRegistrationDataType } from '@/models/partner';
import { flatMap } from 'lodash';

export type FetchPartnerNumbersAndMainPartnershipNumberInput = FetchPartnerNumbersAndMainPartnershipNumberQueryVariables;

export type FetchPartnerNumbersAndMainPartnershipNumberOutput = FetchPartnerNumbersAndMainPartnershipNumberQuery;

export const fetchPartnerNumbersAndMainPartnershipNumber = async (): Promise<FetchPartnerNumbersAndMainPartnershipNumberOutput> => {
  const response = await queryExecute({
    query: graphql(`
      query FetchPartnerNumbersAndMainPartnershipNumber {
        partners(pagination: { limit: 1000, offset: 0 }, filterEmptyPhoneNumbers: true) {
          count
          rows {
            name
            codeName
            gogoNumber
            extension
            infoForOperator
            subsidyDescription
            streetAddress
            streetAddress2
            city
            state
            zip
            country
            isHideRegistrationLink
            isSelfServeDashboard
          }
        }

        mainPartnershipNumber
      }
    `),
    fetchPolicy: 'no-cache',
  });

  return response.data;
};

// data below is fetched once and cached.
let data: Promise<{
  // Map: phone number -> extension (can be empty string) -> partner info
  partners: PartnerRegistrationDataType[];

  mainPartnershipNumber: string;
}> | null = null;

const fetchData = async () => {
  const fetched = await fetchPartnerNumbersAndMainPartnershipNumber();
  if (fetched.mainPartnershipNumber == null) {
    throw new Error('prefetchPartnerNumbers: mainPartnershipNumber not found');
  }

  const filtered = flatMap(
    fetched.partners.rows,
    ({
      name,
      codeName,
      gogoNumber,
      extension,
      subsidyDescription,
      infoForOperator,
      streetAddress,
      streetAddress2,
      city,
      state,
      zip,
      country,
      isHideRegistrationLink,
      isSelfServeDashboard,
    }) => {
      if (!name || !codeName || !gogoNumber) return [];

      const address = concatStrings(' ', [streetAddress, streetAddress2, city, state, zip, country]);

      return [
        {
          name,
          codeName,
          registrationLink: `https://gogograndparent.com/${codeName}`,
          infoForOperator: infoForOperator ?? undefined,
          subsidyDescription: subsidyDescription ?? undefined,
          gogoNumber,
          extension: extension ?? undefined,
          address,
          isHideRegistrationLink: isHideRegistrationLink ?? false,
          isSelfServeDashboard: isSelfServeDashboard ?? false,
        },
      ];
    },
  );

  return {
    mainPartnershipNumber: fetched.mainPartnershipNumber,
    partners: filtered,
  };
};

export const getPartners = async () => {
  if (data == null) {
    data = fetchData();
  }
  return (await data).partners;
};

export const getMainPartnershipNumber = async () => {
  if (data == null) {
    data = fetchData();
  }
  return (await data).mainPartnershipNumber;
};

function concatStrings(joinString: string, strings: Array<string | null | undefined>) {
  const parts = strings.filter((str) => str != null) as string[];
  if (parts.length === 0) {
    return undefined;
  }
  return parts.join(joinString);
}
